import React, { Component } from 'react'
import * as Icons from 'react-feather'
import Dropzone from 'react-dropzone'
import { withStyles } from '@material-ui/core/styles'
import { styles } from '../../styles/client'
import { requestAPI } from '../../data/api.js'
import { getApiBase } from '../../config'
import Loading from '../../generic/Loading'
import { formatDisplayUrl } from './Violation'

class ScreenshotReviewCard extends Component {
	constructor(props) {
		super(props)
		const api_base = getApiBase()
		const hasScreenshot = !!props.violation.capture_method
		this.state = {
			fullsizeImage: hasScreenshot
				? `${api_base}/violations/screenshot-review/${props.violation.violation_id}/screenshot`
				: null,
			hasScreenshot,
			manualUploaded: false,
			capture_method: props.violation.capture_method,
			screenshot_captured_at: props.violation.screenshot_captured_at,
			loadingScreenshot: false,
			completing: false,
			error: null,
		}
	}

	handleMouseEnter = (e) => {
		const img = e.target.querySelector('img')
		if (!img) return
		img.style.visibility = 'hidden'
		this.setState({ backgroundImage: `url(${img.src})` })
	}

	handleMouseMove = (e) => {
		const { left, top, width, height } = e.target.getBoundingClientRect()
		const x = ((e.pageX - left) / width) * 100
		const y = ((e.pageY - top) / height) * 100
		this.setState({ backgroundPosition: `${x}% ${y}%` })
	}

	handleMouseLeave = (e) => {
		const img = e.target.querySelector('img')
		if (img) img.style.visibility = 'visible'
	}

	renderDnsLink = () => {
		const { domain } = this.props.violation
		if (!domain) return null
		let url = `https://www.whois.com/whois/${domain}`
		if (domain.endsWith('.be')) {
			url = `https://www.dnsbelgium.be/nl/whois/info/${domain}/details`
		} else if (domain.endsWith('.nl')) {
			url = `https://www.sidn.nl/whois/?q=${domain}/details`
		} else if (domain.endsWith('.eu')) {
			url = `https://whois.eurid.eu/en/search/?domain=${domain}`
		}
		return (
			<p>
				URL:{' '}
				<a
					href={url}
					target='_blank'
					rel='noopener noreferrer'
					style={{ color: 'blue', textDecoration: 'underline' }}
				>
					{domain}
				</a>
			</p>
		)
	}

	renderAgeWarning = () => {
		const ts = this.state.screenshot_captured_at
		if (!ts) return null
		const ageMs = Date.now() - new Date(ts).getTime()
		const oneMonthMs = 30 * 24 * 60 * 60 * 1000
		if (ageMs <= oneMonthMs) return null
		const days = Math.floor(ageMs / (24 * 60 * 60 * 1000))
		const months = Math.floor(days / 30)
		const ageText =
			months >= 2 ? `${months} months` : days === 1 ? '1 day' : `${days} days`
		return (
			<div
				style={{
					background: '#fff3cd',
					border: '1px solid #ffc107',
					borderRadius: '4px',
					padding: '8px 12px',
					marginTop: '8px',
					display: 'flex',
					alignItems: 'center',
					gap: '8px',
				}}
			>
				<Icons.AlertTriangle size={16} color='#856404' />
				<span style={{ color: '#856404', fontSize: '0.875em' }}>
					Screenshot is {ageText} old
				</span>
			</div>
		)
	}

	promiseFileReader = (photo) =>
		new Promise((resolve) => {
			const reader = new FileReader()
			reader.onload = (event) => resolve(event.target.result)
			reader.readAsDataURL(photo)
		})

	onUploadPhoto = async (photo) => {
		this.setState({ loadingScreenshot: true, error: null })
		const fileReaded = await this.promiseFileReader(photo)
		const response = await requestAPI(
			`/violations/upload_screenshot/${this.props.violation.violation_id}`,
			'POST',
			{
				screenshot: fileReaded.split('base64,')[1],
				last_modified: photo.lastModified,
				name: photo.name,
			},
		)
		if (response && response.status) {
			this.setState({ loadingScreenshot: false, error: 'Upload failed, try again' })
			return
		}
		this.setState({
			loadingScreenshot: false,
			fullsizeImage: fileReaded,
			hasScreenshot: true,
			manualUploaded: true,
			capture_method: 'manual',
		})
	}

	onDrop = (acceptedFiles) => {
		if (acceptedFiles.length > 0) this.onUploadPhoto(acceptedFiles[0])
	}

	rejectViolation = async () => {
		const res = await requestAPI(
			`/violations/set-action/${this.props.violation.violation_id}`,
			'POST',
			{ action: { is_violating: false }, rejectReason: 'other' },
		)
		if (res && res.code) {
			throw new Error(res.message || 'Failed to reject violation')
		}
	}

	completeReview = async (outcome) => {
		this.setState({ completing: true, error: null })
		try {
			if (outcome === 'unrecoverable') {
				await this.rejectViolation()
			}
			const res = await requestAPI(
				`/violations/screenshot-review/${this.props.violation.violation_id}/complete`,
				'POST',
				{ outcome },
			)
			if (res && res.error) {
				this.setState({ completing: false, error: res.error })
				return
			}
			this.props.onReviewed(this.props.violation.violation_id, outcome)
		} catch (err) {
			this.setState({
				completing: false,
				error: err.message || 'Failed to record outcome',
			})
		}
	}

	render() {
		const { violation } = this.props
		const {
			fullsizeImage,
			hasScreenshot,
			manualUploaded,
			capture_method,
			completing,
		} = this.state

		return (
			<div className='violation' id={violation.violation_id}>
				<div className='row' style={{ border: '1px solid black' }}>
					<div className='col-md-8'>
						<Dropzone
							onDrop={this.onDrop}
							acceptStyle={{ opacity: 0.3 }}
							rejectStyle={{ background: 'red' }}
							accept='image/jpeg, image/png'
							style={{
								border: hasScreenshot ? '' : '3px dashed #d32f2f',
								textAlign: 'center',
								cursor: 'pointer',
								background: hasScreenshot ? '' : '#fff5f5',
							}}
						>
							{hasScreenshot && fullsizeImage ? (
								<figure
									onMouseMove={this.handleMouseMove}
									onMouseEnter={this.handleMouseEnter}
									onMouseLeave={this.handleMouseLeave}
									style={{
										backgroundPosition: this.state.backgroundPosition,
										backgroundImage: this.state.backgroundImage,
									}}
								>
									<img
										alt=''
										src={fullsizeImage}
										onError={() =>
											this.setState({
												hasScreenshot: false,
												fullsizeImage: null,
												capture_method: null,
											})
										}
										width='100%'
										style={{
											opacity: this.state.loadingScreenshot ? 0.5 : 1,
											pointerEvents: 'none',
										}}
									/>
								</figure>
							) : (
								<div style={{ padding: '80px 20px', color: '#856404' }}>
									<Icons.AlertTriangle
										size={48}
										color='#d32f2f'
										style={{ marginBottom: '12px' }}
									/>
									<h2 style={{ margin: '0 0 8px', color: '#d32f2f' }}>
										No screenshot available
									</h2>
									<p style={{ margin: 0, opacity: 0.7 }}>
										Drop a manual screenshot here, or reject the case if the
										content is no longer on site.
									</p>
								</div>
							)}
						</Dropzone>
						{this.renderAgeWarning()}
					</div>
					<div
						className='col-md-4'
						style={{ borderLeft: '1px solid black', padding: '10px' }}
					>
						<h2 style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
							<a
								href={violation.url_raw}
								target='_blank'
								rel='noopener noreferrer'
							>
								<p className='url'>
									{violation.favicon && (
										<img alt='' height='20' src={violation.favicon} />
									)}{' '}
									{formatDisplayUrl(violation.url_raw)}
								</p>
							</a>
						</h2>
						{this.renderDnsLink()}
						<p style={{ fontSize: '0.85em', fontFamily: 'monospace', color: 'rgba(0,0,0,0.6)' }}>
							Violation ID: {violation.violation_id}
						</p>
						<p style={{ fontSize: '0.85em', color: 'rgba(0,0,0,0.6)' }}>
							Capture method: <code>{capture_method || 'unknown'}</code>
						</p>
						{violation.image_id && (
							<>
								<p>Original image:</p>
								<img
									src={`${getApiBase()}/images/${violation.image_id}/600`}
									width='100%'
									alt='Original protected image'
								/>
							</>
						)}
						{violation.destination_image_url && (
							<>
								<p>Destination image:</p>
								<img
									src={violation.destination_image_url}
									className='destinationImage'
									width='100%'
									alt=''
								/>
							</>
						)}
						<div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<button
								className='btn primary'
								disabled={completing || !hasScreenshot}
								title={
									hasScreenshot
										? ''
										: 'No screenshot available — upload one or reject the case'
								}
								style={
									!hasScreenshot
										? {
												opacity: 0.5,
												cursor: 'not-allowed',
												background: '#bdbdbd',
												borderColor: '#bdbdbd',
											}
										: {}
								}
								onClick={(e) => {
									e.preventDefault()
									this.completeReview(
										manualUploaded ? 'manual_uploaded' : 'approved',
									)
								}}
							>
								{manualUploaded ? 'Confirm uploaded screenshot' : 'Approve screenshot'}
								{!hasScreenshot && (
									<span style={{ fontSize: '0.75em', display: 'block', opacity: 0.9 }}>
										(upload a screenshot first)
									</span>
								)}
							</button>
							<button
								className='btn secondary'
								disabled={completing}
								style={{ border: '2px solid #d32f2f', color: '#d32f2f' }}
								onClick={(e) => {
									e.preventDefault()
									this.completeReview('unrecoverable')
								}}
							>
								Reject case (No longer on site)
							</button>
							{completing && <Loading width='15' />}
							{this.state.error && (
								<span style={{ color: 'red', fontSize: '0.85em' }}>
									{this.state.error}
								</span>
							)}
							<p style={{ fontSize: '0.8em', opacity: 0.6, margin: '4px 0 0' }}>
								Drop a file on the image to upload a manual replacement.
							</p>
						</div>
					</div>
				</div>
			</div>
		)
	}
}

export default withStyles(styles, { withTheme: true })(ScreenshotReviewCard)
